import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { supabase } from "@/lib/supabaseClient";

type AdminUser = {
  id: string;
  email: string;
  role: "super_admin";
  loginTime: string;
};

type AdminAuthContextType = {
  adminUser: AdminUser | null;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
    twoFactorCode: string
  ) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
};

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(
  undefined
);

export const AdminAuthProvider = ({ children }: { children: ReactNode }) => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on mount with timeout
    const checkSession = async () => {
      try {
        console.log("🔍 Checking admin session...");

        // Check if Supabase is properly configured
        if (!supabase) {
          console.warn("⚠️ Supabase client not available");
          setIsLoading(false);
          return;
        }

        // For development, allow access without authentication
        console.log("📝 Development mode: allowing admin access");
        setAdminUser({
          id: "dev-admin",
          email: "admin@triploko.com",
          role: "super_admin",
          loginTime: new Date().toISOString(),
        });
        setIsLoading(false);
        return;

        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.warn("⚠️ Error getting session:", sessionError);
          setIsLoading(false);
          return;
        }

        if (session?.user) {
          console.log("👤 Admin session found, verifying role...");

          // Try to verify user role with timeout and fallback
          try {
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Role check timeout")), 5000)
            );

            const roleCheckPromise = supabase
              .from("users")
              .select("role")
              .eq("id", session.user.id)
              .single();

            const { data: userProfile, error } = (await Promise.race([
              roleCheckPromise,
              timeoutPromise,
            ])) as any;

            if (!error && userProfile?.role === "super_admin") {
              console.log("✅ Admin role verified");
              setAdminUser({
                id: session.user.id,
                email: session.user.email || "",
                role: "super_admin",
                loginTime: new Date().toISOString(),
              });
            } else {
              console.log("⚠️ User is not super admin or error:", error);
              // For development, allow access if user exists (fallback)
              if (session.user.email) {
                console.log("📝 Development fallback: allowing admin access");
                setAdminUser({
                  id: session.user.id,
                  email: session.user.email,
                  role: "super_admin",
                  loginTime: new Date().toISOString(),
                });
              }
            }
          } catch (roleError) {
            console.warn("⚠️ Role check failed:", roleError);
            // For development, allow access if user exists (fallback)
            if (session.user.email) {
              console.log(
                "📝 Development fallback: allowing admin access due to role check failure"
              );
              setAdminUser({
                id: session.user.id,
                email: session.user.email,
                role: "super_admin",
                loginTime: new Date().toISOString(),
              });
            }
          }
        } else {
          console.log("📝 No admin session found");
        }
      } catch (error) {
        console.warn("⚠️ Error checking admin session:", error);
      } finally {
        console.log("🏁 Admin session check completed");
        setIsLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: any, session: any) => {
      console.log("🔄 Admin auth state changed:", event);
      if (event === "SIGNED_IN" && session?.user) {
        // Try to verify user role with timeout and fallback
        try {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Role check timeout")), 5000)
          );

          const roleCheckPromise = supabase
            .from("users")
            .select("role")
            .eq("id", session.user.id)
            .single();

          const { data: userProfile, error } = (await Promise.race([
            roleCheckPromise,
            timeoutPromise,
          ])) as any;

          if (!error && userProfile?.role === "super_admin") {
            console.log("✅ Admin role verified on sign in");
            setAdminUser({
              id: session.user.id,
              email: session.user.email || "",
              role: "super_admin",
              loginTime: new Date().toISOString(),
            });
          } else {
            console.log(
              "⚠️ User is not super admin or error on sign in:",
              error
            );
            // For development, allow access if user exists (fallback)
            if (session.user.email) {
              console.log(
                "📝 Development fallback: allowing admin access on sign in"
              );
              setAdminUser({
                id: session.user.id,
                email: session.user.email,
                role: "super_admin",
                loginTime: new Date().toISOString(),
              });
            }
          }
        } catch (error) {
          console.warn("⚠️ Role check failed on sign in:", error);
          // For development, allow access if user exists (fallback)
          if (session.user.email) {
            console.log(
              "📝 Development fallback: allowing admin access due to role check failure on sign in"
            );
            setAdminUser({
              id: session.user.id,
              email: session.user.email,
              role: "super_admin",
              loginTime: new Date().toISOString(),
            });
          }
        }
      } else if (event === "SIGNED_OUT") {
        console.log("👋 Admin signed out");
        setAdminUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (
    email: string,
    password: string,
    twoFactorCode: string
  ): Promise<boolean> => {
    try {
      console.log("🔐 Attempting admin login...");

      // Check if Supabase is properly configured
      if (!supabase) {
        console.warn("⚠️ Supabase client not available");
        return false;
      }

      // For demo purposes, we'll still use the demo credentials
      // In production, you would integrate with a real 2FA system
      if (
        email === "admin@test.com" &&
        password === "admin123" &&
        twoFactorCode === "123456"
      ) {
        // Sign in with Supabase
        const { data, error } = await supabase.auth.signInWithPassword({
          email: "admin@test.com",
          password: "admin123", // This should be the real admin password
        });

        if (error) {
          console.error("❌ Supabase login error:", error);
          return false;
        }

        if (data.user) {
          console.log("✅ User authenticated, verifying role...");

          // Try to verify user role with timeout and fallback
          try {
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Role check timeout")), 5000)
            );

            const roleCheckPromise = supabase
              .from("users")
              .select("role")
              .eq("id", data.user.id)
              .single();

            const { data: userProfile, error: profileError } =
              (await Promise.race([roleCheckPromise, timeoutPromise])) as any;

            if (!profileError && userProfile?.role === "super_admin") {
              console.log("✅ Admin role verified successfully");
              setAdminUser({
                id: data.user.id,
                email: data.user.email || "",
                role: "super_admin",
                loginTime: new Date().toISOString(),
              });
              return true;
            } else {
              console.log("⚠️ User is not super admin or error:", profileError);
              // For development, allow access if user exists (fallback)
              if (data.user.email) {
                console.log("📝 Development fallback: allowing admin access");
                setAdminUser({
                  id: data.user.id,
                  email: data.user.email,
                  role: "super_admin",
                  loginTime: new Date().toISOString(),
                });
                return true;
              }
              await supabase.auth.signOut();
              return false;
            }
          } catch (roleError) {
            console.warn("⚠️ Role verification failed:", roleError);
            // For development, allow access if user exists (fallback)
            if (data.user.email) {
              console.log(
                "📝 Development fallback: allowing admin access due to role check failure"
              );
              setAdminUser({
                id: data.user.id,
                email: data.user.email,
                role: "super_admin",
                loginTime: new Date().toISOString(),
              });
              return true;
            }
            await supabase.auth.signOut();
            return false;
          }
        }
      }
      return false;
    } catch (error) {
      console.error("❌ Login error:", error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setAdminUser(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AdminAuthContext.Provider
      value={{
        adminUser,
        isAuthenticated: !!adminUser,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
};
